import { authStateAtom } from '../models/atoms/auth_atoms';
import {
  deviceListStateAtom,
  selectedDeviceStateAtom,
  filterStateAtom,
  requestIDAtom,
  dateRangeAtom,
  selectedDeviceDataAtom,
  systemTypesAtom,
  waterLevelFilteredAtom,
  deviceModeAtom,
  type DeviceMode,
  periodStartDateAtom,
  periodEndDateAtom,
} from '../models/atoms/device_atoms';
import { getRecoil, setRecoil } from 'recoil-nexus';
import {
  deviceListInterface,
  deviceInterface,
  deviceDataInterface,
} from '../models/interfaces/device_interfaces';
import dayjs from 'dayjs';
import {
  debouncedInternalServerErrorAlert,
  debouncedOfflineAlert,
  debouncedSubdomainAlert,
} from '../utils/debouncedAlerts';
import {
  isFetchingDeviceListAtom,
  isFetchingDeviceDataAtom,
} from '../models/atoms/ui_atoms';

const device_link = import.meta.env.REACT_APP_DEVICE_API_URL;

export const device_controller = {
  get_device_list: async function (
    callback = () => {},
    forceMode?: DeviceMode,
  ) {
    const mode = forceMode || getRecoil(deviceModeAtom);
    try {
      const filter = getRecoil(filterStateAtom);
      // Ensure filter and filter.labels are defined, this was breaking the test
      if (!filter || !filter.labels) {
        console.error('Filter or filter.labels is undefined');
        return;
      }

      setRecoil(isFetchingDeviceListAtom, state => ({
        ...state,
        [mode]: true,
      }));

      const params = new URLSearchParams({
        pinned: filter.pinned?.toString() || '',
        mode,
      });

      filter.labels.forEach(label => {
        params.append('labels', label);
      });

      const obj = {
        link: `${device_link}/devices?${params.toString()}`,
        object: {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${getRecoil(authStateAtom).token}`,
          },
        },
      };

      const response = await fetch(obj.link, obj.object);
      if (response.status >= 500) {
        debouncedInternalServerErrorAlert();
        setRecoil(isFetchingDeviceListAtom, state => ({
          ...state,
          [mode]: false,
        }));
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        console.error(
          'Failed to fetch device list:',
          response.status,
          response.statusText,
        );
        console.error(data);
        setRecoil(isFetchingDeviceListAtom, state => ({
          ...state,
          [mode]: false,
        }));
        return;
      }

      const deviceList = data as deviceListInterface;
      setRecoil(deviceListStateAtom, state => {
        const newState: deviceListInterface = {
          ...state,
          search_filter: deviceList.search_filter,
          has_client_identifier: deviceList.has_client_identifier,
        };
        if (mode === 'archived') {
          newState.archivedDevices = deviceList.devices;
        } else {
          newState.devices = deviceList.devices;
        }
        return newState;
      });
      setRecoil(systemTypesAtom, types => {
        const newTypes = new Set<string>(Array.from(types));
        deviceList.devices.forEach(device => {
          newTypes.add(device.system_type);
        });
        return newTypes;
      });
      setRecoil(isFetchingDeviceListAtom, state => ({
        ...state,
        [mode]: false,
      }));
      callback();
    } catch (error) {
      console.error('Error fetching device list:', error);

      setRecoil(isFetchingDeviceListAtom, state => ({
        ...state,
        [mode]: false,
      }));

      if (!navigator.onLine) {
        debouncedOfflineAlert();
        return;
      }

      if (error instanceof TypeError) {
        debouncedSubdomainAlert();
      }
    }
  },

  get_battery_life: async function ({
    readingPeriod,
    readingPeriodUnit,
    transmissionPeriod,
    transmissionPeriodUnit,
    sensorType,
  }: {
    readingPeriod: number;
    readingPeriodUnit: number;
    transmissionPeriod: number;
    transmissionPeriodUnit: number;
    sensorType: 'ultrasound' | 'radar';
  }) {
    try {
      const params = new URLSearchParams({
        measurement_freq: readingPeriod.toString(),
        measurement_unit: readingPeriodUnit.toString(),
        transmission_freq: transmissionPeriod.toString(),
        transmission_unit: transmissionPeriodUnit.toString(),
        sensor_type: sensorType,
      });
      const link = `${device_link}/device/battery_calculator?${params.toString()}`;
      const response = await fetch(link);
      return Number(await response.text()).toFixed(1);
    } catch (error) {
      console.error('Error fetching battery life:', error);
      return '-1';
    }
  },

  select_device: async function (
    device_id: string,
    session_id?: string | null,
  ) {
    const params = new URLSearchParams({
      device_id: device_id,
    });
    if (session_id) {
      params.append('session_id', session_id);
    }
    const obj = {
      link: device_link + '/device?' + params.toString(),
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    await fetch(obj.link, obj.object)
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Error fetching device: ${response.statusText}`);
        }
        const data = (await response.json()) as deviceInterface;
        return data;
      })
      .then(device => {
        setRecoil(selectedDeviceStateAtom, device);
        setRecoil(dateRangeAtom, {
          start: dayjs((device.first_data ?? 0) * 1000).startOf('day'),
          end: dayjs((device.last_data ?? 0) * 1000).endOf('day'),
        });
        setRecoil(
          periodStartDateAtom,
          dayjs((device.last_data ?? 0) * 1000).startOf('day'),
        );
        setRecoil(
          periodEndDateAtom,
          dayjs((device.last_data ?? 0) * 1000).endOf('day'),
        );
        this.get_device_data(
          device_id,
          new Date((device.first_data ?? 0) * 1000).setUTCHours(0, 0, 0) / 1000,
          new Date((device.last_data ?? 0) * 1000).setUTCHours(23, 59, 59) /
            1000,
          device.session_id,
        );
        return device;
      });
  },

  get_device_data: async function (
    device_id: string,
    measurement_period_start_date: number,
    measurement_period_end_date: number,
    session_id?: string | null,
  ) {
    const request_id = getRecoil(requestIDAtom) + 1;
    setRecoil(requestIDAtom, request_id);
    setRecoil(selectedDeviceDataAtom, null);
    setRecoil(isFetchingDeviceDataAtom, true);
    let nextIndex: number | undefined = 0;
    do {
      const params = new URLSearchParams({
        device_id: device_id,
        measurement_period_start_date: measurement_period_start_date.toString(),
        measurement_period_end_date: measurement_period_end_date.toString(),
        index: nextIndex.toString(),
        limit: `${Number.MAX_SAFE_INTEGER}`,
        request_id: request_id.toString(),
        filter_water_level: getRecoil(waterLevelFilteredAtom).toString(),
      });
      if (session_id) {
        params.append('session_id', session_id);
      }
      const link: string =
        device_link + '/device/data-range?' + params.toString();
      const obj = {
        link,
        object: {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
          },
        },
      };
      const device: deviceDataInterface = await (
        await fetch(obj.link, obj.object)
      ).json();
      const selectedDevice = getRecoil(selectedDeviceStateAtom);
      const current_request_id = getRecoil(requestIDAtom);
      if (
        !selectedDevice ||
        device.device_id !== selectedDevice.device_id ||
        current_request_id !== device.request_id
      ) {
        return;
      }
      nextIndex = device.next_index;
      setRecoil(
        selectedDeviceDataAtom,
        (device_data: deviceDataInterface | null) => {
          if (!device_data) {
            return device;
          }
          return {
            ...device_data,
            water_data: [...device_data.water_data, ...device.water_data],
            rainfall_data: [
              ...device_data.rainfall_data,
              ...device.rainfall_data,
            ],
          };
        },
      );
    } while (nextIndex);
    setRecoil(isFetchingDeviceDataAtom, false);
  },

  downloadCSV: async function (
    deviceIDs: Array<{ device_id: string; session_id?: string | null }>,
    start_date: number,
    end_date: number,
    show_rainfall: boolean,
    show_water_level: boolean,
    show_filtered_water_level: boolean,
    file_type: string,
    language: string,
    utc_offset_minutes = 0,
  ) {
    const url = new URL(`${device_link}/device/file`);
    url.searchParams.append(
      'measurement_period_start_date',
      (start_date / 1000).toString(),
    );
    url.searchParams.append(
      'measurement_period_end_date',
      (end_date / 1000).toString(),
    );
    url.searchParams.append('show_rainfall_level', show_rainfall.toString());
    url.searchParams.append('show_water_level', show_water_level.toString());
    url.searchParams.append(
      'show_filtered_water_level',
      show_filtered_water_level.toString(),
    );
    url.searchParams.append('show_imperial', (language === 'en-US').toString());
    url.searchParams.append('file_type', file_type);
    url.searchParams.append(
      'utc_offset_minutes',
      utc_offset_minutes.toString(),
    );

    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(deviceIDs),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
      },
    });

    const disposition = res.headers.get('Content-Disposition');
    if (!disposition) {
      return;
    }

    let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
    if (filename.toLowerCase().startsWith("utf-8''"))
      filename = decodeURIComponent(filename.replace("utf-8''", ''));
    else filename = filename.replace(/['"]/g, '');

    const blob = await res.blob();
    if (!blob) {
      return;
    }

    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  },

  async hide_device(device_id: string, session_id?: string) {
    const url = new URL(`${device_link}/device/hide`);
    url.searchParams.append('device_id', device_id);
    if (session_id) {
      url.searchParams.append('session_id', session_id);
    }
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${getRecoil(authStateAtom).token}`,
        },
      });
      const data = (await response.json()) as deviceInterface;
      setRecoil(selectedDeviceStateAtom, data);
      await this.get_device_list();
    } catch (error) {
      console.error('Error hiding device:', error);
    }
  },

  async toggle_device_pin(device_id: string, session_id?: string | null) {
    const url = new URL(`${device_link}/device`);
    url.searchParams.append('device_id', device_id);
    if (session_id) {
      url.searchParams.append('session_id', session_id);
    }

    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
      },
    });
    const device = (await response.json()) as deviceInterface;

    await fetch(`${device_link}/device`, {
      method: 'PUT',
      body: JSON.stringify({
        device_id,
        session_id,
        pinned: !device.pinned,
      }),
      headers: {
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
        'Content-type': 'application/json',
      },
    });

    this.get_device_list();
    this.select_device(device_id, session_id);
  },

  change_device_comments: function (
    device_id: string,
    comments: string,
    callback = () => {},
  ) {
    const obj = {
      link: device_link + '/device',
      object: {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          device_id,
          comments,
        }),
      },
    };
    fetch(obj.link, obj.object).then(() => {
      this.get_device_list();
      callback();
      this.select_device(device_id);
    });
  },

  change_device_warning_level_percentage: async function (
    device_id: string,
    warning_level_percentage: number,
  ) {
    await fetch(`${device_link}/device`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({
        device_id,
        warning_level_percentage,
      }),
    });

    this.get_device_list();
    this.select_device(device_id);
  },

  changeClientIdentifier: async function (
    deviceID: string,
    clientIdentifier: string,
  ) {
    const putHeaders = {
      'Accept': 'application/json',
      'Authorization': `Bearer ${getRecoil(authStateAtom).token}`,
      'Content-type': 'application/json',
    };
    await fetch(`${device_link}/device`, {
      method: 'PUT',
      headers: putHeaders,
      body: JSON.stringify({
        device_id: deviceID,
        client_identifier: clientIdentifier,
      }),
    });

    this.get_device_list();
    this.select_device(deviceID);
  },

  async uploadImage(deviceID: string, images: File[]) {
    const formData = images.reduce((fd, image) => {
      fd.append('photos', image);
      return fd;
    }, new FormData());

    const headers = {
      Authorization: `Bearer ${getRecoil(authStateAtom).token}`,
    };

    const res = await fetch(`${device_link}/images/${deviceID}`, {
      method: 'PATCH',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const data = await res.json();
      console.error(`Failed to upload photos: ${data}`);
      throw new Error('Failed to upload photos', { cause: data?.detail });
    }
  },

  async changeDeviceLatitudeLongitude(
    deviceID: string,
    latitude: number,
    longitude: number,
  ) {
    const putHeaders = {
      'Accept': 'application/json',
      'Authorization': `Bearer ${getRecoil(authStateAtom).token}`,
      'Content-type': 'application/json',
    };
    await fetch(`${device_link}/device`, {
      method: 'PUT',
      headers: putHeaders,
      body: JSON.stringify({
        device_id: deviceID,
        latitude,
        longitude,
      }),
    });

    this.get_device_list();
    this.select_device(deviceID);
  },
};
